// Shared option sets & helpers for the 5 Partner inquiry forms.
// Keeping the option strings identical to the canonical spec ensures
// submissions are properly categorizable on the LAMKO side.

// Unified Industry sector list used across all 5 forms.
export const INDUSTRY_SECTORS = [
  'OLED / display materials',
  'Semiconductor materials',
  'Electronic chemicals',
  'Battery / energy materials',
  'Specialty polymers & coatings',
  'Flame-retardant chemicals',
  'Master batches',
  'Pharmaceutical intermediates',
  'Fine chemicals (general)',
  'Other (please specify)',
];

// Comprehensive alphabetical country list with priority countries first.
// Used for "Country of incorporation (Headquarters)" single-select and
// "Country of subsidiaries/branches/incorporation/JVs" multi-select.
const _PRIORITY_COUNTRIES = ['India', 'South Korea', 'Japan'];
const _ALPHABETICAL_COUNTRIES = [
  'Afghanistan', 'Albania', 'Algeria', 'Andorra', 'Angola', 'Antigua and Barbuda',
  'Argentina', 'Armenia', 'Australia', 'Austria', 'Azerbaijan', 'Bahamas', 'Bahrain',
  'Bangladesh', 'Barbados', 'Belarus', 'Belgium', 'Belize', 'Benin', 'Bhutan',
  'Bolivia', 'Bosnia and Herzegovina', 'Botswana', 'Brazil', 'Brunei', 'Bulgaria',
  'Burkina Faso', 'Burundi', 'Cabo Verde', 'Cambodia', 'Cameroon', 'Canada',
  'Central African Republic', 'Chad', 'Chile', 'Colombia', 'Comoros',
  'Congo (Democratic Republic)', 'Congo (Republic)', 'Costa Rica', "Cote d'Ivoire",
  'Croatia', 'Cuba', 'Cyprus', 'Czech Republic', 'Denmark', 'Djibouti', 'Dominica',
  'Dominican Republic', 'Ecuador', 'Egypt', 'El Salvador', 'Equatorial Guinea',
  'Eritrea', 'Estonia', 'Eswatini', 'Ethiopia', 'Fiji', 'Finland', 'France',
  'Gabon', 'Gambia', 'Georgia', 'Germany', 'Ghana', 'Greece', 'Grenada',
  'Guatemala', 'Guinea', 'Guinea-Bissau', 'Guyana', 'Haiti', 'Honduras',
  'Hong Kong', 'Hungary', 'Iceland', 'Indonesia', 'Iran', 'Iraq', 'Ireland',
  'Israel', 'Italy', 'Jamaica', 'Jordan', 'Kazakhstan', 'Kenya', 'Kiribati',
  'Kuwait', 'Kyrgyzstan', 'Laos', 'Latvia', 'Lebanon', 'Lesotho', 'Liberia',
  'Libya', 'Liechtenstein', 'Lithuania', 'Luxembourg', 'Macao', 'Madagascar',
  'Malawi', 'Malaysia', 'Maldives', 'Mali', 'Malta', 'Marshall Islands',
  'Mauritania', 'Mauritius', 'Mexico', 'Micronesia', 'Moldova', 'Monaco',
  'Mongolia', 'Montenegro', 'Morocco', 'Mozambique', 'Myanmar', 'Namibia',
  'Nauru', 'Nepal', 'Netherlands', 'New Zealand', 'Nicaragua', 'Niger',
  'Nigeria', 'North Korea', 'North Macedonia', 'Norway', 'Oman', 'Pakistan',
  'Palau', 'Palestine', 'Panama', 'Papua New Guinea', 'Paraguay', 'Peru',
  'Philippines', 'Poland', 'Portugal', 'Qatar', 'Romania', 'Russia', 'Rwanda',
  'Saint Kitts and Nevis', 'Saint Lucia', 'Saint Vincent and the Grenadines',
  'Samoa', 'San Marino', 'Sao Tome and Principe', 'Saudi Arabia', 'Senegal',
  'Serbia', 'Seychelles', 'Sierra Leone', 'Singapore', 'Slovakia', 'Slovenia',
  'Solomon Islands', 'Somalia', 'South Africa', 'South Sudan', 'Spain',
  'Sri Lanka', 'Sudan', 'Suriname', 'Sweden', 'Switzerland', 'Syria',
  'Taiwan', 'Tajikistan', 'Tanzania', 'Thailand', 'Timor-Leste', 'Togo',
  'Tonga', 'Trinidad and Tobago', 'Tunisia', 'Turkey', 'Turkmenistan', 'Tuvalu',
  'Uganda', 'Ukraine', 'United Arab Emirates', 'United Kingdom', 'United States',
  'Uruguay', 'Uzbekistan', 'Vanuatu', 'Vatican City', 'Venezuela', 'Vietnam',
  'Yemen', 'Zambia', 'Zimbabwe',
];
export const COUNTRY_LIST = [
  ..._PRIORITY_COUNTRIES,
  ..._ALPHABETICAL_COUNTRIES,
  'Other (please specify)',
];

export const HOW_HEARD_OPTIONS = [
  'Industry conference / exhibition',
  'Referral from colleague',
  'Web search',
  'LinkedIn / social media',
  'Trade publication',
  'Existing partnership',
  'Other',
];

// -----------------------------------------------------------------
// FORM 6: BECOME A PARTNER
// -----------------------------------------------------------------
export const BECOME_PARTNER_OPTIONS = {
  countryIncorporation: [
    'India', 'South Korea', 'Japan', 'Taiwan', 'Vietnam',
    'United States', 'Germany', 'United Kingdom', 'France', 'Switzerland',
    'Other (please specify)',
  ],
  employeeCount: ['1-10', '11-50', '51-200', '201-500', '501-1,000', '1,000+'],
  revenueRange: [
    '< $1M', '$1M-$5M', '$5M-$25M', '$25M-$100M', '$100M+', 'Prefer not to disclose',
  ],
  preferredLanguage: [
    'English', 'Korean (한국어)', 'Japanese (日本語)', 'Chinese (中文)', 'No preference',
  ],
  partnerType: [
    'CRO (Contract Research Organization) - R&D / custom synthesis lab',
    'CMO (Contract Manufacturing Organization) - production factory',
    'CDMO (Contract Development & Manufacturing) - development + production',
    'Purification services provider - sublimation / recrystallization / chromatography',
    'Analytical testing laboratory',
    'Toll processing / toll manufacturing',
    'Equipment / technology provider',
    'Raw material / intermediate supplier',
    'Multiple types (describe in notes below)',
  ],
  servicesOffered: [
    'Custom synthesis (mg to g scale)',
    'Custom synthesis (g to kg scale)',
    'Custom synthesis (kg to MT scale)',
    'Process development & optimization',
    'Scale-up from lab to pilot',
    'Pilot batch manufacturing',
    'Commercial-scale manufacturing',
    'Sublimation purification (single-pass)',
    'Sublimation purification',
    'Recrystallization',
    'Column chromatography purification',
    'Preparative HPLC / SFC purification',
    'Adsorption / metal scavenger treatment',
    'Zone refining',
    'Analytical testing & method development',
    'ICP-MS / ICP-OES trace metal analysis',
    'Quality control & COA issuance',
    'Packaging & labeling (cleanroom / inert atmosphere)',
    'Warehousing & logistics',
    'Regulatory & compliance support',
    'Other (specify in notes)',
  ],
  industrySectorsServed: [
    'OLED / display materials',
    'Semiconductor materials',
    'Electronic chemicals',
    'Battery / energy materials',
    'Specialty polymers & coatings',
    'Flame-retardant chemicals',
    'Master batches',
    'Fine chemicals (general)',
    'Other',
  ],
  materialClasses: [
    'OLED emitters (fluorescent / phosphorescent / TADF)',
    'OLED host materials',
    'OLED transport materials (HTL / ETL / HIL / EIL)',
    'Organic semiconductor monomers',
    'Organic semiconductor polymers / oligomers',
    'Photoresist materials',
    'Electron transport / hole transport small molecules',
    'Organometallic complexes (Ir, Pt, Pd-based)',
    'Heterocyclic building blocks',
    'Aromatic / polyaromatic compounds',
    'Halogenated intermediates',
    'Fluorinated compounds',
    'Flame-retardant additives',
    'Polymer additives & master batch compounds',
    'Inorganic semiconductor precursors',
    'General fine chemical intermediates',
    'Other (specify in notes)',
  ],
  chemistryCapabilities: [
    'Suzuki coupling',
    'Buchwald-Hartwig amination',
    'Ullmann coupling',
    'Heck reaction',
    'Stille coupling',
    'Grignard reactions',
    'Friedel-Crafts (alkylation / acylation)',
    'Nucleophilic aromatic substitution',
    'Halogenation (Cl, Br, I, F)',
    'Nitration / reduction',
    'Hydrogenation (catalytic)',
    'Organolithium chemistry',
    'Cyclization reactions',
    'Condensation reactions',
    'Wittig / HWE reactions',
    'Metal-catalyzed C-H activation',
    'High-temperature reactions (> 200°C)',
    'Cryogenic reactions (< -40°C)',
    'High-pressure reactions',
    'Photochemistry',
    'Electrochemistry',
    'Other (specify in notes)',
  ],
  reactorCapacity: [
    'Lab scale only (< 5 L)',
    'Bench / kilo lab (5-50 L)',
    'Pilot scale (50-500 L)',
    'Small production (500 L-2,000 L)',
    'Medium production (2,000-10,000 L)',
    'Large production (10,000 L+)',
    'Multiple scales available',
  ],
  purificationEquipment: [
    'Sublimation furnace (horizontal tube)',
    'Sublimation furnace (vertical / multi-zone)',
    'Zone sublimation / gradient sublimation system',
    'Vacuum sublimation system',
    'Rotary evaporators',
    'Short-path distillation',
    'Column chromatography systems',
    'Preparative HPLC',
    'Preparative SFC',
    'Recrystallization vessels (jacketed)',
    'Nutsche filter / filter dryer',
    'Centrifuge',
    'Spray dryer',
    'Cleanroom packaging facility',
    'Glove box / inert atmosphere handling',
    'Not applicable',
    'Other (specify in notes)',
  ],
  analyticalInstruments: [
    'HPLC',
    'GC / GC-MS',
    'LC-MS',
    'NMR (specify MHz in notes)',
    'ICP-MS',
    'ICP-OES',
    'GD-MS',
    'TXRF',
    'UV-Vis spectrophotometer',
    'Fluorescence / PL spectrometer',
    'DSC / TGA',
    'XRD',
    'Karl Fischer titration',
    'Melting point apparatus',
    'Elemental analyzer (CHN/CHNS)',
    'Ion chromatography',
    'Particle size analyzer',
    'SEM / TEM',
    'Other (specify in notes)',
  ],
  qualityCertifications: [
    'ISO 9001',
    'ISO 14001',
    'ISO 45001',
    'GMP / cGMP',
    'REACH registered facility',
    'K-REACH compliance',
    'RoHS compliant manufacturing',
    'TSCA compliant',
    'EcoVadis rated',
    'IATF 16949 (automotive)',
    'FAMI-QS',
    'No formal certifications',
    'In process of obtaining (specify in notes)',
    'Other (specify in notes)',
  ],
  facilityAudits: [
    'Yes - regularly audited by customers',
    'Yes - third-party audit within last 12 months',
    'Yes - but more than 12 months ago',
    'No - but open to audits',
    'No',
  ],
  cleanroom: [
    'ISO Class 1',
    'ISO Class 2',
    'ISO Class 3 (Class 1)',
    'ISO Class 4 (Class 10)',
    'ISO Class 5 (Class 100)',
    'ISO Class 6 (Class 1,000)',
    'ISO Class 7 (Class 10,000)',
    'ISO Class 8 (Class 100,000)',
    'Controlled environment (non-classified)',
    'No cleanroom facility',
    'Not applicable',
  ],
  ehsCapabilities: [
    'Hazardous material handling license',
    'Solvent recovery / recycling system',
    'Waste water treatment facility',
    'Air emission control (scrubber / VOC treatment)',
    'Fire suppression systems for chemical storage',
    'Explosion-proof equipment / ATEX zones',
    'None of the above',
  ],
  capacityUtilization: [
    '< 25% (significant spare capacity)',
    '25-50%',
    '50-75%',
    '75-90%',
    '> 90% (near full capacity)',
    'Prefer not to disclose',
  ],
  availability: [
    'Immediately',
    'Within 1 month',
    'Within 3 months',
    'Within 6 months',
    'Depends on project scope',
  ],
  partnershipInterest: [
    'Project-based contracts (per-project engagement)',
    'Long-term supply / manufacturing agreement',
    'Preferred partner / approved vendor listing',
    'Joint development partnership',
    'Toll manufacturing arrangement',
    'Technology licensing',
    'Open to all arrangements',
  ],
  howHeard: [
    'Industry conference / exhibition',
    'Referral',
    'Web search',
    'LinkedIn / social media',
    'Trade publication',
    'Existing business relationship',
    'Other',
  ],
  ndaRequired: [
    "Yes - please send LAMKO's standard NDA",
    'Yes - we will provide our NDA',
    'Mutual NDA preferred',
    'Not required at this stage',
    'Already have NDA in place with LAMKO',
  ],
};

export const ACCEPTED_FILE_TYPES =
  '.pdf,.doc,.docx,.xls,.xlsx,.cdx,.cdxml,.mol,.sdf';

export const MAX_FILE_SIZE_BYTES = 25 * 1024 * 1024;

// -----------------------------------------------------------------
// FORM 1: CRDMO
// -----------------------------------------------------------------
export const CRDMO_OPTIONS = {
  industrySector: INDUSTRY_SECTORS,
  structureAvailable: [
    'Yes - will upload with this form',
    'Yes - will share under NDA',
    'Partially defined (scaffold known)',
    'No - need assistance with design',
    'Proprietary - discussion required',
  ],
  projectStage: [
    'Literature / patent search',
    'Concept & feasibility assessment',
    'Route scouting & retrosynthesis',
    'Lab-scale synthesis (mg-g)',
    'Process optimization (g-kg)',
    'Ready for scale-up evaluation',
  ],
  requiredGrade: [
    'Synthesis grade (standard purity)',
    'Sublimed grade',
    'Unsublimed grade (pre-sublimation)',
    'Electronic grade',
    'Reagent grade',
    'Custom grade (please specify below)',
  ],
  targetQuantity: [
    'Milligram scale (< 1 g)',
    'Gram scale (1-100 g)',
    'Hectogram scale (100 g - 1 kg)',
    'Kilogram scale (1-10 kg)',
    'To be determined',
  ],
 purityRequirement: [
    '> 95%',
    '> 98%',
    '> 99%',
    '> 99.5% (high purity)',
    '> 99.9% (ultra-high purity)',
    'To be discussed based on application',
  ],
  analyticalNeeds: [
    'NMR (¹H, ¹³C, 2D)',
    'Mass spectrometry (LC-MS/GC-MS)',
    'HPLC purity analysis',
    'Elemental analysis (CHN)',
    'Thermal analysis (TGA/DSC)',
    'UV-Vis / PL spectroscopy',
    'XRD / single crystal analysis',
    'Optical properties (for OLED)',
    'Custom analytical method development',
    'Not sure - need guidance',
  ],
  ipConfidentiality: [
    'Standard NDA is sufficient',
    'Mutual NDA required before discussion',
    'IP assignment to client',
    'Joint IP development',
    'No special requirements',
    'Prefer to discuss',
  ],
  timeline: [
    'Urgent (< 4 weeks)',
    'Standard (1-3 months)',
    'Flexible (3-6 months)',
    'Long-term engagement (6+ months)',
    "Open to LAMKO's recommendation",
  ],
};

// -----------------------------------------------------------------
// FORM 2: CDMO
// -----------------------------------------------------------------
export const CDMO_OPTIONS = {
  industrySector: INDUSTRY_SECTORS,
  developmentStage: [
    'Lab-scale (mg-g) - needs scale-up',
    'Bench-scale (g-100 g) proven',
    'Pilot-scale (100 g - 10 kg) demonstrated',
    'Process transfer from another CDMO',
    'Existing process - seeking optimization',
    'No existing process - full development needed',
  ],
  servicesRequired: [
    'Process development & optimization',
    'Scale-up from lab to pilot',
    'Pilot batch manufacturing (kg-scale)',
    'Analytical method development & validation',
    'Process safety evaluation (HAZOP)',
    'Impurity control & specification setting',
    'Stability / shelf-life testing',
    'Technology transfer support',
    'Regulatory documentation support',
    'Full CDMO (development through commercial)',
  ],
  targetScale: [
    '100 g - 1 kg (pilot)',
    '1 - 10 kg (small-scale)',
    '10 - 100 kg (mid-scale)',
    '100 - 500 kg (large-scale)',
    '500 kg+ (commercial)',
    'Multiple scales / phased approach',
  ],
 puritySpec: [
    '> 95%',
    '> 98%',
    '> 99%',
    '> 99.5%',
    '> 99.9% (electronic grade)',
    '> 99.99% (ultra-high purity)',
    'Custom specification (describe below)',
  ],
  qualityRegulatory: [
    'ISO 9001 compliance',
    'ISO 14001 (environmental)',
    'GMP / cGMP',
    'REACH registration',
    'K-REACH (Korea)',
    'RoHS compliance',
    'TSCA (US EPA)',
    'Customer-specific audit requirements',
    'Electronic grade specifications',
    'None at this stage',
    'Other (please specify)',
  ],
  criticalQualityAttributes: [
    'Purity (HPLC/GC)',
    'Color / appearance',
    'Water content (KF)',
    'Metals content (ICP-MS)',
    'Particle size distribution',
    'Sublimation grade purity',
    'Thermal stability (TGA)',
    'Specific optical properties',
    'Custom impurity limits',
    'Other',
  ],
  timeline: [
    'Urgent (< 6 weeks)',
    'Standard (2-4 months)',
    'Flexible (4-6 months)',
    'Phased program (6-12 months)',
    'Long-term development (12+ months)',
  ],
  annualVolume: [
    '< 10 kg/year',
    '10 - 50 kg/year',
    '50 - 200 kg/year',
    '200 - 1,000 kg/year',
    '1,000+ kg/year',
    'To be determined',
  ],
  budgetRange: [
    '< $25,000 USD',
    '$25,000 - $100,000 USD',
    '$100,000 - $500,000 USD',
    '$500,000 - $1,000,000 USD',
    '> $1,000,000 USD',
    'Prefer not to disclose',
  ],
};

// -----------------------------------------------------------------
// FORM 3: CMO
// -----------------------------------------------------------------
export const CMO_OPTIONS = {
  industrySector: INDUSTRY_SECTORS,
  productStatus: [
    'Fully developed process - ready for tech transfer',
    'Previously manufactured by another CMO',
    'Pilot-validated - first commercial production',
    'Requires minor process adjustments',
    'New product - development support may be needed',
  ],
 puritySpec: [
    '> 95%',
    '> 98%',
    '> 99%',
    '> 99.5%',
    '> 99.9% (electronic grade)',
    '> 99.99% (ultra-high purity)',
    'Sublimation grade',
    'Custom specification',
  ],
  packaging: [
    'Standard laboratory containers',
    'Amber glass bottles (light-sensitive)',
    'Inert atmosphere (N₂/Ar) sealed',
    'Drum packaging (25-200 kg)',
    'Customer-specific packaging',
    'To be discussed',
  ],
  batchSize: [
    '1 - 10 kg',
    '10 - 50 kg',
    '50 - 100 kg',
    '100 - 500 kg',
    '500 kg - 1 MT',
    '1 MT+',
    'Variable / to be discussed',
  ],
  annualDemand: [
    '< 50 kg/year',
    '50 - 200 kg/year',
    '200 - 1,000 kg/year',
    '1 - 5 MT/year',
    '5 - 20 MT/year',
    '20+ MT/year',
    'To be determined',
  ],
  orderFrequency: [
    'One-time order',
    'Quarterly',
    'Monthly',
    'Bi-monthly',
    'As-needed / ad hoc',
    'Long-term supply agreement (1+ years)',
    'Framework contract with call-offs',
  ],
  firstDelivery: [
    '< 4 weeks (urgent)',
    '1 - 2 months',
    '2 - 4 months',
    '4 - 6 months',
    '6+ months (planning phase)',
    'Flexible',
  ],
  qualityStandards: [
    'ISO 9001',
    'ISO 14001',
    'GMP / cGMP',
    'REACH',
    'K-REACH',
    'RoHS',
    'TSCA',
    'Customer quality audit',
    'Electronic grade standards',
    'Conflict minerals reporting',
    'Other',
  ],
  supplyChain: [
    'Safety stock / buffer inventory',
    'Vendor-managed inventory (VMI)',
    'Dedicated production line',
    'Lot traceability & full documentation',
    'Change control notification',
    'Dual-source qualification',
    'Consignment stock arrangement',
    'None specific at this stage',
  ],
  shippingDestination: [
    'South Korea (domestic)',
    'East Asia (Japan, Taiwan)',
    'Southeast Asia',
    'North America',
    'Europe',
    'Multiple regions',
    'Other',
  ],
  pricingModel: [
    'Fixed price per kg',
    'Volume-tiered pricing',
    'Annual contract with fixed pricing',
    'Cost-plus model',
    "Open to LAMKO's proposal",
    'Prefer to discuss',
  ],
};

// -----------------------------------------------------------------
// FORM 4: PURIFICATION
// -----------------------------------------------------------------
export const PURIFICATION_OPTIONS = {
  materialType: [
    'OLED emitter (fluorescent)',
    'OLED emitter (phosphorescent)',
    'OLED emitter (TADF)',
    'OLED host material',
    'OLED hole transport material (HTL)',
    'OLED hole injection material (HIL)',
    'OLED electron transport material (ETL)',
    'OLED electron injection material (EIL)',
    'OLED dopant material',
    'Organic semiconductor monomer',
    'Organic semiconductor polymer/oligomer',
    'Photoresist material',
    'Other semiconductor organic material',
    'Inorganic semiconductor precursor',
    'Other (please specify below)',
  ],
  currentPurity: [
    '< 90%',
    '90 - 95%',
    '95 - 98%',
    '98 - 99%',
    '99 - 99.5%',
    '99.5 - 99.9%',
    '> 99.9%',
    'Unknown - needs analysis first',
  ],
  currentGrade: [
    'Crude synthesis product',
    'Synthesis grade (standard)',
    'Reagent grade',
    'Recrystallized (unsublimed)',
    'Sublimed (single sublimation)',
    'Sublimed (multi-pass)',
    'Electronic grade',
    'Unknown / ungraded',
  ],
  targetPurity: [
    '> 99%',
    '> 99.5%',
    '> 99.9%',
    '> 99.95%',
    '> 99.99%',
    '> 99.999%',
    'Custom target (specify in notes)',
    'LAMKO to recommend based on application',
  ],
  targetGrade: [
    'Sublimed grade (standard)',
    'Sublimed grade (multi-pass / high-purity)',
    'Electronic grade',
    'Device grade (panel-ready)',
    'Unsublimed - recrystallized only',
    'Custom grade (specify below)',
    'LAMKO to recommend',
  ],
  targetIndividualMetal: [
    '< 100 ppm (parts per million)',
    '< 10 ppm',
    '< 1 ppm',
    '< 100 ppb (parts per billion)',
    '< 10 ppb',
    '< 1 ppb',
    '< 100 ppt (parts per trillion)',
    '< 10 ppt',
    'Custom limits per element (specify below)',
    'LAMKO to recommend based on application',
  ],
  totalMetals: [
    '< 1,000 ppm',
    '< 100 ppm',
    '< 10 ppm',
    '< 1 ppm',
    '< 100 ppb',
    '< 10 ppb',
    '< 1 ppb',
    'LAMKO to recommend',
  ],
  metalImpurities: [
    'Li (Lithium)', 'Be (Beryllium)', 'B (Boron)', 'Na (Sodium)', 'Mg (Magnesium)',
    'Al (Aluminum)', 'Si (Silicon)', 'P (Phosphorus)', 'K (Potassium)', 'Ca (Calcium)',
    'Ti (Titanium)', 'V (Vanadium)', 'Cr (Chromium)', 'Mn (Manganese)', 'Fe (Iron)',
    'Co (Cobalt)', 'Ni (Nickel)', 'Cu (Copper)', 'Zn (Zinc)', 'Ga (Gallium)',
    'Ge (Germanium)', 'As (Arsenic)', 'Se (Selenium)', 'Rb (Rubidium)', 'Sr (Strontium)',
    'Y (Yttrium)', 'Zr (Zirconium)', 'Nb (Niobium)', 'Mo (Molybdenum)', 'Ru (Ruthenium)',
    'Rh (Rhodium)', 'Pd (Palladium)', 'Ag (Silver)', 'Cd (Cadmium)', 'In (Indium)',
    'Sn (Tin)', 'Sb (Antimony)', 'Te (Tellurium)', 'Cs (Cesium)', 'Ba (Barium)',
    'La (Lanthanum)', 'Ce (Cerium)', 'Hf (Hafnium)', 'Ta (Tantalum)', 'W (Tungsten)',
    'Re (Rhenium)', 'Ir (Iridium)', 'Pt (Platinum)', 'Au (Gold)', 'Hg (Mercury)',
    'Tl (Thallium)', 'Pb (Lead)', 'Bi (Bismuth)', 'U (Uranium)',
    'All elements - comprehensive panel',
    'LAMKO to recommend based on material type',
  ],
  halideControl: [
    'Yes - Cl (Chloride)',
    'Yes - Br (Bromide)',
    'Yes - I (Iodide)',
    'Yes - F (Fluoride)',
    'Yes - all halides',
    'Not required',
    'LAMKO to assess',
  ],
  purificationTechniques: [
    'Temperature gradient sublimation (single-pass)',
    'Temperature gradient sublimation (multi-pass)',
    'Zone sublimation / fractional sublimation',
    'Vacuum sublimation',
    'Recrystallization (single solvent)',
    'Recrystallization (mixed solvent / anti-solvent)',
    'Column chromatography (silica gel)',
    'Column chromatography (alumina)',
    'Column chromatography (custom stationary phase)',
    'Preparative HPLC',
    'Preparative SFC (supercritical fluid chromatography)',
    'Adsorption treatment (activated carbon)',
    'Adsorption treatment (activated clay / silica)',
    'Adsorption treatment (metal scavenger resins)',
    'Zone refining / zone melting',
    'Solvent washing / liquid-liquid extraction',
    'Chelation / complexation treatment',
    'Soxhlet extraction',
    'Reprecipitation / precipitation purification',
    'Combined multi-step approach',
    'Not sure - LAMKO to recommend optimal method',
    'LAMKO to decide based on material & target specs',
  ],
  analyticalMethods: [
    'ICP-MS (inductively coupled plasma mass spectrometry)',
    'ICP-OES / ICP-AES (optical emission spectrometry)',
    'GD-MS (glow discharge mass spectrometry)',
    'TXRF (total reflection X-ray fluorescence)',
    'HPLC (high-performance liquid chromatography)',
    'GC-MS (gas chromatography-mass spectrometry)',
    'LC-MS (liquid chromatography-mass spectrometry)',
    'SFC (supercritical fluid chromatography)',
    'NMR (¹H, ¹³C, 2D NMR)',
    'DSC / TGA (thermal analysis)',
    'UV-Vis / PL spectroscopy',
    'Mass spectrometry (MALDI-TOF / ESI)',
    'Karl Fischer titration (water content)',
    'Ion chromatography (halide analysis)',
    'Elemental analysis (CHN/CHNS)',
    'LAMKO to recommend appropriate methods',
  ],
  coaRequirements: [
    'Overall purity (HPLC/GC)',
    'Individual metal impurity levels (ICP-MS)',
    'Total metals content',
    'Halide content',
    'Water content (KF)',
    'Melting point / thermal data',
    'Optical properties (Abs/PL spectra)',
    'NMR confirmation',
    'Full impurity identification',
    'Lot traceability information',
    'LAMKO standard COA format',
  ],
  quantityToPurify: [
    '< 100 g',
    '100 g - 500 g',
    '500 g - 1 kg',
    '1 - 5 kg',
    '5 - 10 kg',
    '10 - 50 kg',
    '50 - 100 kg',
    '100+ kg',
    'Multiple batches / ongoing supply',
  ],
  serviceFrequency: [
    'One-time purification',
    'Recurring - monthly',
    'Recurring - quarterly',
    'Recurring - as needed',
    'Long-term purification service agreement',
    'Evaluation run first, then decide',
  ],
  timeline: [
    'Urgent (< 2 weeks)',
    'Standard (2-4 weeks)',
    'Flexible (1-2 months)',
    'Ongoing / scheduled deliveries',
    'LAMKO to advise on turnaround time',
  ],
  materialHandling: [
    'Air-sensitive (requires inert atmosphere)',
    'Moisture-sensitive',
    'Light-sensitive',
    'Temperature-controlled storage',
    'Hazardous / requires special permit',
    'High-value material (special security)',
    'None / standard handling',
  ],
  shippingDestination: [
    'South Korea (domestic)',
    'East Asia (Japan, Taiwan)',
    'Southeast Asia',
    'North America',
    'Europe',
    'Same as origin - return to sender',
    'Other (specify in notes)',
  ],
};

// -----------------------------------------------------------------
// FORM 5: TECHNICAL CONSULTATION
// -----------------------------------------------------------------
export const TECH_CONSULT_OPTIONS = {
  consultationTopic: [
    'OLED material selection & design',
    'Synthesis route evaluation',
    'Process scale-up feasibility',
    'Material sourcing strategy',
    'Regulatory & compliance guidance (REACH, K-REACH)',
    'Quality system & analytical method consulting',
    'Vendor / supplier evaluation',
    'Technology landscape assessment',
    'IP strategy for chemical materials',
    'General R&D project advisory',
    'Other (please describe below)',
  ],
  industryFocus: INDUSTRY_SECTORS,
  scopeComplexity: [
    'Quick question / single topic (1-2 hours)',
    'Focused project review (half-day)',
    'Comprehensive technical audit (1-3 days)',
    'Ongoing advisory retainer (monthly)',
    'Strategic partnership exploration',
    'Not sure - need scoping discussion',
  ],
  format: [
    'Video call (Zoom, Teams, etc.)',
    'Phone call',
    'In-person meeting (LAMKO office, Korea)',
    'On-site visit at your facility',
    'Email exchange / written report',
    'No preference',
  ],
  timezone: [
    'KST (UTC+9) - Korea, Japan',
    'CST (UTC+8) - Taiwan, Singapore',
    'IST (UTC+5:30) - India',
    'CET (UTC+1) - Central Europe',
    'GMT (UTC+0) - UK, Portugal',
    'EST (UTC-5) - US East Coast',
    'CST (UTC-6) - US Central',
    'PST (UTC-8) - US West Coast',
    'Other (please specify in notes)',
  ],
  availability: [
    'This week',
    'Next 1-2 weeks',
    'Within the next month',
    'Flexible - no urgency',
    'Specific date (please note below)',
  ],
  language: [
    'English',
    'Korean (한국어)',
    'Japanese (日本語)',
    'Chinese (中文)',
    'No preference',
  ],
  ndaRequired: [
    "Yes - please send LAMKO's standard NDA",
    'Yes - we will provide our NDA',
    'Mutual NDA preferred',
    'Not required for initial discussion',
    'Already have NDA in place with LAMKO',
  ],
};
